(() => {
  const tagName = 'c-clinical-tabs';
  if (customElements.get(tagName)) return;

  let libraryPromise;
  function loadCharts() {
    if (window.Chart?.version?.startsWith('4.')) return Promise.resolve(window.Chart);
    if (!libraryPromise) {
      libraryPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.8/dist/chart.umd.min.js';
        script.onload = () => resolve(window.Chart);
        script.onerror = () => {
          script.remove();
          libraryPromise = null;
          reject(new Error('Chart library unavailable'));
        };
        document.head.append(script);
      });
    }
    return libraryPromise;
  }

  class ClinicalTabs extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.charts = new Map();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) this.select(this.tabs.indexOf(tab));
      }, options);
      this.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.select(next, true);
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.panels.findIndex((panel) => panel.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index);
      }, options);
      this.motion.addEventListener('change', () => {
        this.charts.forEach((chart) => {
          chart.options.animation = this.animation();
          chart.update('none');
        });
      }, options);
      this.select(0);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.visible = true;
          this.render(this.panels[this.activeIndex]);
          this.observer.disconnect();
        }, { threshold: 0.15 });
        this.observer.observe(this);
      } else {
        this.visible = true;
        this.render(this.panels[this.activeIndex]);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.charts?.forEach((chart) => chart.destroy());
      this.charts?.clear();
    }

    animation() {
      return this.motion.matches ? false : { duration: 1200, easing: 'easeOutQuart' };
    }

    select(index, focus = false) {
      if (!this.panels[index]) return;
      this.activeIndex = index;
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.panels[tabIndex].hidden = !active;
      });
      if (focus) this.tabs[index].focus({ preventScroll: true });
      const tab = this.tabs[index];
      const strip = this.querySelector('[data-tab-scroll]');
      if (strip && strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: tab.offsetLeft - strip.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2, behavior: this.motion.matches ? 'instant' : 'smooth' });
      }
      if (this.visible) this.render(this.panels[index]);
    }

    async render(panel) {
      if (!panel || panel.hidden) return;
      const canvas = panel.querySelector('canvas');
      if (!canvas) return;
      const existing = this.charts.get(panel);
      if (existing) {
        existing.resize();
        existing.reset();
        existing.update();
        return;
      }
      if (panel.dataset.chartLoading === 'true') return;
      panel.dataset.chartLoading = 'true';
      try {
        const ChartLibrary = await loadCharts();
        if (!this.isConnected || panel.hidden) return;
        const data = JSON.parse(panel.querySelector('[data-chart-config]').textContent);
        const styles = getComputedStyle(this);
        const colors = {
          accent: styles.getPropertyValue('--sl-c-clinical-tabs-accent').trim(),
          text: styles.getPropertyValue('--sl-c-clinical-tabs-text').trim(),
          muted: styles.getPropertyValue('--sl-c-clinical-tabs-muted').trim(),
          grid: styles.getPropertyValue('--sl-c-clinical-tabs-grid').trim(),
          track: styles.getPropertyValue('--sl-c-clinical-tabs-track').trim(),
          white: styles.getPropertyValue('--sl-c-clinical-tabs-card').trim()
        };
        const horizontal = data.type === 'horizontal';
        const line = data.type === 'line';
        const compact = canvas.parentElement.clientWidth < 400;
        const maximum = horizontal ? 100 : Math.max(1, Number(data.maximum));
        const values = data.values.map((value) => value === null ? null : Math.min(maximum, Math.max(0, Number(value) || 0)));
        const font = { family: styles.fontFamily, size: horizontal ? 14 : compact ? 9 : 11 };
        const duration = this.motion.matches ? 0 : 1200;
        const decorate = {
          id: 'clinicalLabels',
          beforeDatasetsDraw(chart) {
            if (!horizontal) return;
            const { ctx, chartArea, scales } = chart;
            ctx.save();
            ctx.fillStyle = colors.track;
            values.forEach((value, index) => {
              const center = scales.y.getPixelForValue(index);
              ctx.beginPath();
              ctx.roundRect(chartArea.left, center - 6, chartArea.width, 12, 6);
              ctx.fill();
            });
            ctx.restore();
          },
          afterDatasetsDraw(chart) {
            const { ctx, scales, chartArea } = chart;
            ctx.save();
            if (horizontal) {
              ctx.font = `600 14px ${font.family}`;
              data.labels.forEach((label, index) => {
                const center = scales.y.getPixelForValue(index);
                ctx.textAlign = 'left';
                ctx.fillStyle = colors.text;
                ctx.fillText(label, chartArea.left, center - 18, chartArea.width - 60);
                ctx.textAlign = 'right';
                ctx.fillStyle = colors.accent;
                ctx.font = `600 24px ${font.family}`;
                ctx.fillText(`${values[index]}%`, chartArea.right, center - 18);
                ctx.font = `600 14px ${font.family}`;
              });
            } else if (!line) {
              const bars = chart.getDatasetMeta(0).data;
              bars.forEach((bar, index) => {
                const unknown = values[index] === null;
                const label = index === 0 ? data.placeboLabel : data.valueLabel;
                let top = bar.y;
                if (unknown) {
                  top = scales.y.getPixelForValue(maximum * 0.21);
                  ctx.fillStyle = colors.track;
                  ctx.strokeStyle = colors.muted;
                  ctx.setLineDash([3, 3]);
                  ctx.beginPath();
                  ctx.roundRect(bar.x - bar.width / 2, top, bar.width, chartArea.bottom - top, [8, 8, 0, 0]);
                  ctx.fill();
                  ctx.stroke();
                  ctx.setLineDash([]);
                }
                ctx.font = `500 ${compact ? 10 : 13}px ${font.family}`;
                const width = Math.min(ctx.measureText(label).width + 26, chartArea.width / 2 - 8);
                const left = Math.max(chartArea.left, Math.min(bar.x - width / 2, chartArea.right - width));
                const pillHeight = compact ? 24 : 30;
                const labelTop = Math.max(2, top - pillHeight - 12);
                ctx.fillStyle = unknown ? colors.white : colors.text;
                ctx.beginPath();
                ctx.roundRect(left, labelTop, width, pillHeight, pillHeight / 2);
                ctx.fill();
                if (unknown) {
                  ctx.strokeStyle = colors.muted;
                  ctx.setLineDash([2, 2]);
                  ctx.stroke();
                  ctx.setLineDash([]);
                }
                ctx.fillStyle = unknown ? colors.muted : colors.white;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(label, left + width / 2, labelTop + pillHeight / 2, width - 14);
              });
            }
            ctx.restore();
          }
        };
        const chart = new ChartLibrary(canvas, {
          type: line ? 'line' : 'bar',
          data: {
            labels: data.labels,
            datasets: [{
              data: line ? data.hours.map((hour, index) => ({ x: hour, y: values[index] })) : values,
              backgroundColor: line ? (context) => {
                if (!context.chart.chartArea) return colors.accent;
                const gradient = context.chart.ctx.createLinearGradient(0, context.chart.chartArea.top, 0, context.chart.chartArea.bottom);
                gradient.addColorStop(0, `${colors.accent}40`);
                gradient.addColorStop(1, `${colors.accent}00`);
                return gradient;
              } : horizontal ? colors.accent : [colors.track, colors.accent],
              borderColor: line ? colors.accent : [colors.muted, colors.accent],
              borderWidth: line ? 3 : 0,
              borderRadius: horizontal ? 20 : { topLeft: 10, topRight: 10 },
              borderSkipped: false,
              barPercentage: 0.64,
              categoryPercentage: 0.7,
              barThickness: horizontal ? 12 : undefined,
              maxBarThickness: 128,
              fill: line,
              cubicInterpolationMode: 'monotone',
              pointRadius: line ? (context) => context.dataIndex === 1 ? 6 : context.dataIndex === values.length - 1 ? 4 : 0 : 0,
              pointHoverRadius: 7,
              pointBackgroundColor: colors.white,
              pointBorderWidth: 3,
              animations: horizontal && duration ? { x: { duration, delay: (context) => context.dataIndex * 160 } } : undefined
            }]
          },
          plugins: [decorate],
          options: {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: horizontal ? 'y' : 'x',
            animation: this.animation(),
            layout: { padding: { top: horizontal ? 34 : 32, bottom: 4, left: line ? 10 : 0, right: line ? 10 : 0 } },
            interaction: { mode: 'nearest', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: colors.text,
                titleFont: font,
                bodyFont: font,
                displayColors: false,
                callbacks: {
                  title: (items) => line ? `${items[0].parsed.x}h` : items[0].label,
                  label: (context) => line ? `+${context.parsed.y}%` : horizontal ? `${context.parsed.x}%` : context.dataIndex === 1 ? data.valueLabel : `${context.parsed.y}${data.unit ? ` ${data.unit}` : ''}`
                }
              }
            },
            scales: {
              x: horizontal ? { display: false, min: 0, max: 100 } : {
                type: line ? 'linear' : 'category',
                min: line ? 0 : undefined,
                max: line ? 24 : undefined,
                afterBuildTicks: line ? (scale) => { scale.ticks = data.hours.map((hour) => ({ value: hour })); } : undefined,
                grid: { display: false },
                border: { color: colors.text, width: 2 },
                ticks: { font, color: colors.text, maxRotation: 0, autoSkip: false, callback: line ? (value) => `${value}h` : function (value) { return this.getLabelForValue(value); } }
              },
              y: horizontal ? { display: false, offset: true } : {
                min: 0,
                max: maximum,
                position: 'right',
                border: { display: false },
                grid: { color: (context) => context.tick.value === 0 ? 'transparent' : colors.grid, drawTicks: false },
                ticks: { stepSize: maximum / (line ? 4 : 3), mirror: !compact, padding: 2, font, color: colors.muted, callback: (value) => value === 0 ? '' : `${line ? '+' : ''}${Math.round(value * 100) / 100}${data.unit === '%' ? '%' : value === maximum ? ` ${data.unit}` : ''}` }
              }
            }
          }
        });
        this.charts.set(panel, chart);
        panel.dataset.chartReady = 'true';
      } catch {
        panel.dataset.chartReady = 'false';
      } finally {
        delete panel.dataset.chartLoading;
      }
    }
  }

  customElements.define(tagName, ClinicalTabs);
})();